import { either, option, record, struct, task, taskOption } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { formatMessage } from '../../domain/formatMessage.js';
import { logChildLabels, mergeChildContext } from '../../domain/LogContext.js';
import { logLevelsConfig } from '../../domain/LogLevel.js';
import { isNonEmptyRecord, recordValues, taskRun } from '../../internal/prelude.js';
import { extractSourceMappingUrl, withMappedStackTrace } from './mapStackTrace.js';
/**
 * Creates a logger instance that can be used in browsers.
 *
 * The "publish" function can be used for sending logs to a server, if needed.
 */
export const makeBrowserLogger = (publish) => {
    const sourceMapCache = {};
    const httpFetchSourceMap = (uri) => pipe(sourceMapCache, record.lookup(uri), option.match(() => pipe(taskOption.tryCatch(() => fetch(uri).then((r) => r.text())), taskOption.chainOptionK(extractSourceMappingUrl(uri)), taskOption.chain((url) => taskOption.tryCatch(() => fetch(url).then((r) => r.text()))), task.chainFirstIOK((map) => () => void (sourceMapCache[uri] = map))), task.of));
    const mapStackTraces = (x) => async () => {
        if (Array.isArray(x))
            await taskRun(task.traverseArray(mapStackTraces)(x));
        if (x instanceof Error)
            await taskRun(withMappedStackTrace(x, httpFetchSourceMap));
    };
    return makeLogger({ mapStackTraces, publish });
};
export const makeLogger = ({ mapStackTraces, publish }) => {
    const log = (context) => (level) => (message, currentExtra) => {
        const { [logChildLabels]: labels, ...parentExtra } = context;
        const extra = pipe({ ...parentExtra, ...currentExtra }, option.fromPredicate((x) => isNonEmptyRecord(x)), option.toUndefined);
        const { left: errors, right: restExtra } = parseExtra(extra);
        // eslint-disable-next-line no-console
        console[level](...[formatMessage(message, labels), ...errors, restExtra].filter(Boolean));
        if (publish != null) {
            void (async () => {
                if (mapStackTraces != null && extra != null) {
                    await pipe(extra, record.traverse(task.ApplicativePar)(mapStackTraces), taskRun);
                }
                await taskRun(publish(level, message, extra));
            })();
        }
    };
    const mkContextLogger = (parentContext) => ({
        ...pipe(logLevelsConfig, record.mapWithIndex(log(parentContext))),
        child: flow(mergeChildContext(parentContext), mkContextLogger),
    });
    return mkContextLogger({});
};
/**
 * Separates unknown extra data into Array<Error> and a record of the rest.
 *
 * We can use this to log errors with the browser's console directly, which leads
 * to much better formatting.
 */
const parseExtra = (extra) => pipe(extra ?? {}, record.map((x) => x instanceof Error ? either.left(x) : either.right(x)), record.separate, struct.evolve({
    left: (x) => recordValues(x),
    right: (x) => (isNonEmptyRecord(x) ? x : undefined),
}));
//# sourceMappingURL=logger.js.map